import { Circle, PlaylistPlay } from '@mui/icons-material';
import { Box, Button, Divider, Grid, Paper, Stack, SxProps, Typography } from '@mui/material';
import { t } from 'i18next';
import TaniumFinished from '../../../assets/images/tanium_finished.svg';
import TaniumNotRun from '../../../assets/images/tanium_not_started.svg';
import TaniumRunning from '../../../assets/images/tanium_progress.svg';
import {
  ACTIVE_STATUSES,
  NO_COMPLETED_RETRIEVAL_STATUSES,
  PENDING_STATUSES,
  TaniumStatus,
  getStatusColor,
  getStatusLabel,
} from '../../../integrations/tanium/enums/TaniumStatus';
import { ProjectTanium } from '../../../integrations/tanium/models/ProjectTanium';
import { DangerButton } from '../../../modal-flows/components/Buttons';

type Props = {
  isLoading: boolean;
  isOrgIntegrationConfigured: boolean;
  projectTanium: ProjectTanium | null;
  canUpdate: boolean;
  handleStartTanium: () => void;
  handleStopTanium: () => void;
  handleGetLog: () => void;
};

export const STYLES = {
  title: { textTransform: 'uppercase', color: 'text.secondary', fontWeight: 500 },
  secondary: { color: 'text.secondary', fontSize: '.875rem' },
  sessionSubtitle: { pt: 1 },
} satisfies Record<string, SxProps>;

export const ProjectTaniumSession = ({
  isLoading,
  isOrgIntegrationConfigured,
  projectTanium,
  canUpdate,
  handleStartTanium,
  handleStopTanium,
  handleGetLog,
}: Props) => {
  const completedAt = projectTanium?.getCompletedAt();
  const status = projectTanium?.getStatus() ?? null;
  const computerGroup = projectTanium?.getComputerGroup();

  const formattedCompletedAt = completedAt ? new Date(completedAt).toUTCString() : '';
  const isTaniumRetrievalActive = status !== null && ACTIVE_STATUSES.includes(status);
  const noCompletedRetrieval = NO_COMPLETED_RETRIEVAL_STATUSES.includes(status);
  const isPendingState = status !== null && PENDING_STATUSES.includes(status);
  const allButtonsDisabled = isPendingState || !isOrgIntegrationConfigured || isLoading || !canUpdate;

  const displayLogDownloadButton = status !== null && !isLoading;

  return (
    <Paper sx={{ backgroundColor: 'backdrops.lightGrey', padding: 3, height: '100%' }}>
      <Stack direction={'column'} spacing={2}>
        <Stack sx={{ minHeight: '19rem' }} direction={'column'} spacing={2}>
          <Stack direction={'row'} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography sx={STYLES.title}>{t('projectIntegrations.tanium.currentSession')}</Typography>
            {isTaniumRetrievalActive ? (
              <DangerButton onClick={handleStopTanium} disabled={allButtonsDisabled}>
                {t('projectIntegrations.tanium.stop')}
              </DangerButton>
            ) : (
              <Button
                variant="contained"
                startIcon={<PlaylistPlay />}
                color="success"
                onClick={handleStartTanium}
                disabled={!computerGroup || allButtonsDisabled}
              >
                {t('projectIntegrations.tanium.launchRetrieval')}
              </Button>
            )}
          </Stack>
          <Box sx={{ textAlign: 'center', p: 5 }}>
            {noCompletedRetrieval && (
              <>
                <TaniumNotRun role="img" title={t('a11y.project.integrations.notRunningLogo')} />
                <Typography sx={[STYLES.secondary, STYLES.sessionSubtitle]}>{t('projectIntegrations.tanium.noPrevRuns')}</Typography>
              </>
            )}
            {status === TaniumStatus.COMPLETED && (
              <>
                <TaniumFinished role="img" title={t('a11y.project.integrations.taniumCompletedLogo')} />
                <Typography sx={[STYLES.secondary, STYLES.sessionSubtitle]}>{t('projectIntegrations.tanium.completedRun')}</Typography>
              </>
            )}
            {isTaniumRetrievalActive && <TaniumRunning role="img" title={t('a11y.project.integrations.taniumRunningLogo')} />}
            {displayLogDownloadButton && (
              <>
                <br />
                <Button onClick={handleGetLog} sx={{ color: 'primary.main' }}>
                  {t('projectIntegrations.tanium.downloadLogs')}
                </Button>
              </>
            )}
          </Box>
        </Stack>
        <Divider />
        {status !== null && (
          <Grid container>
            <Grid item xs={3}>
              <Stack direction={'column'} spacing={1}>
                <Typography sx={STYLES.secondary}>{t('projectIntegrations.tanium.statusLabel')}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Circle sx={{ fontSize: '1rem', pr: 0.5, color: getStatusColor(status) }} />
                  <Typography data-role="tanium-status">{getStatusLabel(status)}</Typography>
                </Box>
              </Stack>
            </Grid>
            {isTaniumRetrievalActive && (
              <>
                <Grid item xs={3}>
                  <Stack direction={'column'} spacing={1}>
                    <Typography sx={STYLES.secondary}>{t('projectIntegrations.tanium.endpoints')}</Typography>
                    <Typography data-role="tanium-endpoints">{projectTanium?.getEndpointsCount() ?? t('projectIntegrations.tanium.emptyData')}</Typography>
                  </Stack>
                </Grid>
                <Grid item xs={3}>
                  <Stack direction={'column'} spacing={1}>
                    <Typography sx={STYLES.secondary}>{t('projectIntegrations.tanium.remaining')}</Typography>
                    <Typography data-role="tanium-todo">{projectTanium?.getTodoCount() ?? t('projectIntegrations.tanium.emptyData')}</Typography>
                  </Stack>
                </Grid>
              </>
            )}
            {!isTaniumRetrievalActive && projectTanium?.getCompletedAt() !== null && (
              <Grid item xs={6}>
                <Stack direction={'column'} spacing={1}>
                  <Typography sx={STYLES.secondary}>{t('projectIntegrations.tanium.completedAt')}</Typography>
                  <Typography data-role="tanium-completed-at">{formattedCompletedAt}</Typography>
                </Stack>
              </Grid>
            )}
          </Grid>
        )}
      </Stack>
    </Paper>
  );
};
